/**
 * School theme palette.
 *
 * A principal picks one colour for their school, the primary colour: buttons,
 * navigation, headings. The lighter shades are derived from it with the same
 * hue/lightness steps as the app's default palette, so the default navy gives
 * exactly today's navy → ocean blue → cyan. The secondary colour (accents) is
 * always the default amber.
 *
 * Everything else follows from the primary colour:
 * - A dark primary keeps the app light: white cards, today's greys.
 * - A light primary turns the app dark: dark grey cards and page, light text,
 *   greys tinted towards the primary's hue so they sit well with it.
 * Text on the primary colour is white or near-black, whichever reads better,
 * and links and the light shade are kept readable on the cards.
 *
 * The result feeds the CSS custom properties declared in
 * src/styles/_variables.scss ($theme-defaults).
 */

export const THEME_TOKENS = [
  'primary-main',
  'primary-light',
  'primary-lighter',
  'primary-lightest',
  'on-primary',
  'secondary-main',
  'secondary-light',
  'secondary-lighter',
  'surface',
  'page-bg',
  'neutral-main',
  'neutral-light',
  'neutral-lighter',
  'neutral-lightest',
  'gray-50',
  'gray-400',
  'gray-600',
  'gray-700',
  'gray-800',
  'border',
  'border-strong',
  'link',
  'success-light',
  'error-light',
  'warning-light',
] as const;

export type ThemeToken = (typeof THEME_TOKENS)[number];
export type ThemePalette = Readonly<Record<ThemeToken, string>>;
export type ThemeMode = 'light' | 'dark';

/** Must match $theme-defaults in src/styles/_variables.scss. */
export const DEFAULT_THEME: ThemePalette = {
  'primary-main': '#0B2D72',
  'primary-light': '#0992C2',
  'primary-lighter': '#0AC4E0',
  'primary-lightest': '#EFF6FF',
  'on-primary': '#FFFFFF',
  'secondary-main': '#D97706',
  'secondary-light': '#F6E7BC',
  'secondary-lighter': '#FEF3C7',
  'surface': '#FFFFFF',
  'page-bg': '#F7F8FA',
  'neutral-main': '#111827',
  'neutral-light': '#6B7280',
  'neutral-lighter': '#D1D5DC',
  'neutral-lightest': '#F3F4F6',
  'gray-50': '#F9FAFB',
  'gray-400': '#9CA3AF',
  'gray-600': '#4B5563',
  'gray-700': '#374151',
  'gray-800': '#1F2937',
  'border': '#E5E7EB',
  'border-strong': '#D1D5DC',
  'link': '#0992C2',
  // Status tints (backgrounds behind status colours): fixed in a light app,
  // dark tints in a dark one so the text on them stays readable.
  'success-light': '#D1FAE5',
  'error-light': '#FEE2E2',
  'warning-light': '#FEF3C7',
};

/** Status colours themselves never change ($success-main etc. in _variables.scss). */
const STATUS_MAIN = { 'success-light': '#16A34A', 'error-light': '#DC2626', 'warning-light': '#D97706' } as const;

export const DEFAULT_PRIMARY_COLOR = DEFAULT_THEME['primary-main'];

const WHITE = '#FFFFFF';
const HEX_COLOR = /^#[0-9A-F]{6}$/i;

/** Light/lighter primary shades never go above this lightness, so they stay distinct from the tint. */
const MAX_SHADE_LIGHTNESS = 92;
/** Below this saturation a colour reads as grey: keep its shades grey too. */
const GREY_SATURATION = 5;

/**
 * primary-light doubles as hover/accent text: at least WCAG's 3:1 for large
 * text and UI parts on the cards (the default ocean blue is 3.5:1 on white).
 * Links must meet 4.5:1 for normal text.
 */
const MIN_LIGHT_SHADE_CONTRAST = 3;
const MIN_LINK_CONTRAST = 4.5;

type DarkGrey =
  | 'page-bg' | 'surface' | 'gray-50' | 'neutral-lightest' | 'border' | 'neutral-lighter' | 'border-strong'
  | 'gray-400' | 'neutral-light' | 'gray-600' | 'gray-700' | 'gray-800' | 'neutral-main';

/**
 * Dark mode greys: lightness (HSL %) of each grey token, tinted with the
 * primary's hue. The light greys they replace, inverted.
 */
const DARK_GREYS: Record<DarkGrey, number> = {
  'page-bg': 7,
  'surface': 11,
  'gray-50': 14,
  'neutral-lightest': 17,
  'border': 22,
  'neutral-lighter': 30,
  'border-strong': 30,
  'gray-400': 52,
  'neutral-light': 68,
  'gray-600': 76,
  'gray-700': 84,
  'gray-800': 90,
  'neutral-main': 94,
};
/** How strongly the dark greys take on the primary's hue (HSL saturation %). */
const DARK_GREY_SATURATION = 10;
/** Near-black text on a light primary. */
const DARK_ON_PRIMARY_LIGHTNESS = 9;
/** In dark mode the tinted backgrounds are the colour mixed into the card colour. */
const DARK_TINT = { 'primary-lightest': 0.2, 'secondary-light': 0.32, 'secondary-lighter': 0.2, status: 0.16 };

export interface Hsl { h: number; s: number; l: number }

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR.test(value);
}

/** "#abcdef" → "#ABCDEF"; anything that isn't a 6-digit hex colour → null. */
export function normalizeHex(value: string | null | undefined): string | null {
  return isHexColor(value) ? value.toUpperCase() : null;
}

function toRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function fromRgb(r: number, g: number, b: number): string {
  const part = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

export function hexToHsl(hex: string): Hsl {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s: s * 100, l: l * 100 };
}

export function hslToHex({ h, s, l }: Hsl): string {
  const sat = s / 100;
  const light = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sat * Math.min(light, 1 - light);
  const f = (n: number) => light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return fromRgb(f(0) * 255, f(8) * 255, f(4) * 255);
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** `amount` (0–1) of colour `a` mixed into colour `b`. */
function mix(a: string, b: string, amount: number): string {
  const ca = toRgb(a);
  const cb = toRgb(b);
  return fromRgb(ca[0] * amount + cb[0] * (1 - amount), ca[1] * amount + cb[1] * (1 - amount), ca[2] * amount + cb[2] * (1 - amount));
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a "#RRGGBB" colour. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = toRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two "#RRGGBB" colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Light primaries (where dark text reads better than white) turn the app dark.
 * Picking whichever of white / near-black contrasts more also keeps text on
 * the primary readable.
 */
export function themeMode(primary: string | null | undefined): ThemeMode {
  const p = normalizeHex(primary) ?? DEFAULT_PRIMARY_COLOR;
  return contrastRatio(p, DEFAULT_THEME['neutral-main']) > contrastRatio(p, WHITE) ? 'dark' : 'light';
}

/**
 * The default palette's lighter shades move towards brighter hues (navy →
 * ocean blue → cyan, i.e. towards yellow, the brightest hue).
 * Do the same for any colour: turn by the same number of degrees towards
 * yellow, the short way round, without passing it.
 */
const BRIGHTEST_HUE = 60;

function towardsBrightest(h: number, degrees: number): number {
  const delta = ((BRIGHTEST_HUE - h + 540) % 360) - 180; // signed, -180..180
  const turn = Math.sign(delta) * Math.min(Math.abs(degrees), Math.abs(delta));
  return (h + turn + 360) % 360;
}

interface Step { dh: number; ds: number; dl: number; l: number }

/** The steps from a default colour to one of its default shades. */
function stepBetween(from: string, to: string): Step {
  const a = hexToHsl(from);
  const b = hexToHsl(to);
  return { dh: b.h - a.h, ds: b.s - a.s, dl: b.l - a.l, l: b.l };
}

const PRIMARY_STEPS = {
  light: stepBetween(DEFAULT_PRIMARY_COLOR, DEFAULT_THEME['primary-light']),
  lighter: stepBetween(DEFAULT_PRIMARY_COLOR, DEFAULT_THEME['primary-lighter']),
  lightest: stepBetween(DEFAULT_PRIMARY_COLOR, DEFAULT_THEME['primary-lightest']),
};

function shade(base: Hsl, st: Step, lightness: number): string {
  const grey = base.s < GREY_SATURATION;
  return hslToHex({
    h: towardsBrightest(base.h, st.dh),
    s: grey ? base.s : clamp(base.s + st.ds, 0, 100),
    l: clamp(lightness, 0, 100),
  });
}

/**
 * Moves a colour's lightness away from the background until it reaches the
 * contrast ratio, keeping its hue: darker on light backgrounds, lighter on
 * dark ones.
 */
function readableOn(hex: string, background: string, min: number): string {
  if (contrastRatio(hex, background) >= min) return hex;
  const hsl = hexToHsl(hex);
  const darken = relativeLuminance(background) > 0.18;
  for (let l = hsl.l; darken ? l >= 0 : l <= 100; l += darken ? -1 : 1) {
    const candidate = hslToHex({ ...hsl, l });
    if (contrastRatio(candidate, background) >= min) return candidate;
  }
  return darken ? '#000000' : WHITE;
}

type PrimaryShades = Pick<ThemePalette, 'primary-main' | 'primary-light' | 'primary-lighter' | 'primary-lightest'>;

function primaryShades(p: string): PrimaryShades {
  if (p === DEFAULT_PRIMARY_COLOR) {
    // Exactly today's shades, without HSL round-trip drift.
    const d = DEFAULT_THEME;
    return { 'primary-main': d['primary-main'], 'primary-light': d['primary-light'], 'primary-lighter': d['primary-lighter'], 'primary-lightest': d['primary-lightest'] };
  }
  const base = hexToHsl(p);
  return {
    'primary-main': p,
    'primary-light': shade(base, PRIMARY_STEPS.light, Math.min(base.l + PRIMARY_STEPS.light.dl, MAX_SHADE_LIGHTNESS)),
    'primary-lighter': shade(base, PRIMARY_STEPS.lighter, Math.min(base.l + PRIMARY_STEPS.lighter.dl, MAX_SHADE_LIGHTNESS)),
    // The tint is always as light as the default one, whatever the primary.
    'primary-lightest': shade(base, PRIMARY_STEPS.lightest, PRIMARY_STEPS.lightest.l),
  };
}

function darkGreys(primaryHsl: Hsl): Pick<ThemePalette, DarkGrey> {
  const s = primaryHsl.s < GREY_SATURATION ? 0 : DARK_GREY_SATURATION;
  const out = {} as Record<DarkGrey, string>;
  for (const token of Object.keys(DARK_GREYS) as DarkGrey[]) {
    out[token] = hslToHex({ h: primaryHsl.h, s, l: DARK_GREYS[token] });
  }
  return out;
}

/**
 * The full palette for a school's colour. Null (or an invalid value) means the
 * default.
 */
export function buildPalette(primary: string | null | undefined): ThemePalette {
  const p = normalizeHex(primary) ?? DEFAULT_PRIMARY_COLOR;
  if (p === DEFAULT_PRIMARY_COLOR) return DEFAULT_THEME;

  const pShades = primaryShades(p);

  if (themeMode(p) === 'light') {
    // Dark primary: today's light app; only the primary colours change.
    const surface = DEFAULT_THEME['surface'];
    return {
      ...DEFAULT_THEME,
      ...pShades,
      'primary-light': readableOn(pShades['primary-light'], surface, MIN_LIGHT_SHADE_CONTRAST),
      'link': readableOn(pShades['primary-light'], surface, MIN_LINK_CONTRAST),
    };
  }

  // Light primary: a dark app.
  const base = hexToHsl(p);
  const greys = darkGreys(base);
  const surface = greys['surface'];
  const secondary = DEFAULT_THEME['secondary-main'];
  return {
    ...DEFAULT_THEME,
    ...pShades,
    ...greys,
    'on-primary': hslToHex({ h: base.h, s: base.s < GREY_SATURATION ? 0 : DARK_GREY_SATURATION, l: DARK_ON_PRIMARY_LIGHTNESS }),
    'primary-light': readableOn(pShades['primary-light'], surface, MIN_LIGHT_SHADE_CONTRAST),
    'primary-lightest': mix(p, surface, DARK_TINT['primary-lightest']),
    'secondary-light': mix(secondary, surface, DARK_TINT['secondary-light']),
    'secondary-lighter': mix(secondary, surface, DARK_TINT['secondary-lighter']),
    'link': readableOn(pShades['primary-light'], surface, MIN_LINK_CONTRAST),
    'success-light': mix(STATUS_MAIN['success-light'], surface, DARK_TINT.status),
    'error-light': mix(STATUS_MAIN['error-light'], surface, DARK_TINT.status),
    'warning-light': mix(STATUS_MAIN['warning-light'], surface, DARK_TINT.status),
  };
}

/** True when the palette is the app's default one. */
export function isDefaultPalette(palette: ThemePalette): boolean {
  return THEME_TOKENS.every((t) => palette[t] === DEFAULT_THEME[t]);
}

// ── Startup screen ──────────────────────────────────────────────────────────

/**
 * Colours of the startup screen in src/index.html (shown before the app's
 * code loads), keyed like its `--al-*` CSS variables. Glow colours are
 * "r, g, b" triplets because the page applies its own transparency.
 */
export interface SplashColors {
  bg: string;
  glow1: string;
  glow2: string;
  glow3: string;
  bar: string;
  logo: string;
}

/** Must match the `--al-*` defaults in src/index.html. */
export const DEFAULT_SPLASH: Readonly<SplashColors> = {
  bg: '#091E52',
  glow1: '9, 100, 195',
  glow2: '10, 196, 224',
  glow3: '30, 60, 180',
  bar: '9, 196, 224',
  logo: '9, 146, 194',
};

/** The default startup background is the navy at this share of its lightness, capped so white text stays crisp. */
const SPLASH_BG_LIGHTNESS_RATIO = 0.72;
const SPLASH_BG_MAX_LIGHTNESS = 18;

/** The default glows are 40–46% light; a very dark primary's shades would vanish into the background. */
const SPLASH_GLOW_MIN_LIGHTNESS = 40;

const triplet = (hex: string) => toRgb(hex).join(', ');
const glow = (hex: string) => {
  const hsl = hexToHsl(hex);
  return triplet(hsl.l >= SPLASH_GLOW_MIN_LIGHTNESS ? hex : hslToHex({ ...hsl, l: SPLASH_GLOW_MIN_LIGHTNESS }));
};

/**
 * The startup screen in the school's colour: a deep shade of the primary
 * behind glows in its lighter shades. The default colour gives exactly
 * today's startup screen.
 */
export function splashColors(primary: string | null | undefined): SplashColors {
  const p = normalizeHex(primary) ?? DEFAULT_PRIMARY_COLOR;
  if (p === DEFAULT_PRIMARY_COLOR) return DEFAULT_SPLASH;
  const palette = buildPalette(p);
  const base = hexToHsl(p);
  return {
    bg: hslToHex({ ...base, l: Math.min(base.l * SPLASH_BG_LIGHTNESS_RATIO, SPLASH_BG_MAX_LIGHTNESS) }),
    glow1: glow(palette['primary-light']),
    glow2: glow(palette['primary-lighter']),
    glow3: glow(mix(palette['primary-light'], p, 0.5)),
    bar: glow(palette['primary-lighter']),
    logo: glow(palette['primary-light']),
  };
}
